import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  FormControlLabel,
  MenuItem,
  Select,
  type SelectChangeEvent,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Flag } from "@phosphor-icons/react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  type ChartData,
  type ChartOptions,
  type TooltipModel,
} from "chart.js";
import { Line } from "react-chartjs-2";

import { buildTrendData } from "./computeTrendLine";
import { buildPhaseZonePlugin } from "./plugins/phaseZonePlugin";
import { buildMasteryCriterionPlugin } from "./plugins/masteryCriterionPlugin";
import { buildMarkerPlugin } from "./plugins/markerPlugin";
import { useMarkerInteractions } from "./useMarkerInteractions";
import MarkerActionsMenu from "../MarkerActionsMenu";
import type { ChartMarkerLine, TargetChartProps, XAxisMode } from "./TargetChart.types";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip);

// Types and the canvas plugins live alongside this file; re-exported here so
// consumers keep importing them from `TargetChart`.
export * from "./TargetChart.types";

const X_AXIS_MODE_OPTIONS: { value: XAxisMode; label: string }[] = [
  { value: "date", label: "Date" },
  { value: "session", label: "Session #" },
];

/** elevation/2 — the layered drop shadow the tooltip uses (Figma token). */
const TOOLTIP_SHADOW =
  "0px 0px 0px 1px rgba(0,0,0,0.06), 0px 1px 1px -0.5px rgba(0,0,0,0.06), 0px 3px 3px -1.5px rgba(0,0,0,0.06), 0px 6px 6px -3px rgba(0,0,0,0.06), 0px 12px 12px -6px rgba(0,0,0,0.06), 0px 24px 24px -12px rgba(0,0,0,0.06)";

interface TooltipState {
  visible: boolean;
  index: number;
  x: number;
  y: number;
}

/** What each marker variant is called in its tooltip. */
const MARKER_TYPE_LABEL: Record<ChartMarkerLine["variant"], string> = {
  "phase-change": "Phase line",
  "external-event": "External event",
};

const HIDDEN_TOOLTIP: TooltipState = { visible: false, index: 0, x: 0, y: 0 };

const fontFamily = "BasierCircle, sans-serif";

/** Shared text style for the hover tooltip rows (Figma: tooltip-label). */
const TOOLTIP_TEXT_SX = {
  fontFamily,
  fontWeight: 500,
  fontSize: "10px",
  lineHeight: "14px",
  color: "text.primary",
} as const;

// ---------------------------------------------------------------------------
// TargetChart
// ---------------------------------------------------------------------------

/**
 * Session-level line chart for target detail views. Shows data points per
 * session, an optional trend line, a mastery criterion threshold, and
 * clinical phase background zones with labels and dividers.
 *
 * @param props
 * @param props.title - Descriptive title above the chart
 * @param props.yAxisLabel - Y-axis label
 * @param props.labels - Session x-axis labels
 * @param props.data - Data values per session
 * @param [props.masteryCriterion] - Mastery threshold (horizontal dashed line)
 * @param [props.phaseZones] - Clinical phase background bands
 * @param [props.onToggleXAxisMode] - X-axis mode (date/session) toggle callback
 * @param [props.showTrendLine] - Whether to display the trend line
 * @param [props.onToggleTrendLine] - Trend line toggle callback
 * @param [props.onAddMarker] - Add marker callback
 * @param [props.onEditMarker] - "Edit marker" callback; makes marker lines clickable
 * @param [props.onDeleteMarker] - "Delete marker" callback; makes marker lines clickable
 */
const TargetChart: React.FC<TargetChartProps> = ({
  title,
  yAxisLabel,
  yScale = "percentage",
  labels,
  data,
  masteryCriterion,
  masteryCriterionLabel,
  phaseZones = [],
  markers = [],
  tooltipPoints,
  xAxisMode = "date",
  onToggleXAxisMode,
  showTrendLine = false,
  onToggleTrendLine,
  onAddMarker,
  onEditMarker,
  onDeleteMarker,
  onActivePointChange,
  onChartReady,
  sx,
}) => {
  const theme = useTheme();
  const chartRef = useRef<ChartJS<"line"> | null>(null);

  const setChartRef = (chart: ChartJS<"line"> | null | undefined) => {
    const normalized = chart ?? null;
    chartRef.current = normalized;
    onChartReady?.(normalized);
  };

  // Cleanup the Chart instance on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      chartRef.current?.destroy();
    };
  }, []);

  // Instead the plugin reads markers from this ref, and we force a redraw whenever they change.
  const markersRef = useRef(markers);
  useEffect(() => {
    markersRef.current = markers;
    chartRef.current?.update?.();
  }, [markers]);

  // Marker lines are hoverable/clickable only when the caller handles an action.
  const {
    hovered: hoveredMarker,
    hoveredMarkerId,
    getHoveredMarkerId,
    menu: markerMenu,
    closeMenu: closeMarkerMenu,
    pointerHandlers: markerPointerHandlers,
  } = useMarkerInteractions({
    chartRef,
    markers,
    enabled: Boolean(onEditMarker || onDeleteMarker),
  });

  // Custom HTML tooltip (designed) — driven by Chart.js's `external` handler,
  // which reports the hovered point's caret position and index. Only used when
  // `tooltipPoints` is provided; otherwise the default Chart.js tooltip renders.
  const useCustomTooltip = (tooltipPoints?.length ?? 0) > 0;
  const [tooltip, setTooltip] = useState<TooltipState>(HIDDEN_TOOLTIP);

  const handleExternalTooltip = useCallback((context: { tooltip: TooltipModel<"line"> }) => {
    const model = context.tooltip;
    if (model.opacity === 0) {
      setTooltip((prev) => (prev.visible ? { ...prev, visible: false } : prev));
      return;
    }
    const activePoint = model.dataPoints?.[0];
    if (!activePoint) {
      setTooltip((prev) => (prev.visible ? { ...prev, visible: false } : prev));
      return;
    }
    const index = activePoint.dataIndex;
    const x = model.caretX;
    const y = model.caretY;
    setTooltip((prev) =>
      prev.visible && prev.index === index && prev.x === x && prev.y === y
        ? prev
        : { visible: true, index, x, y }
    );
  }, []);

  // A hovered marker line takes precedence: its tooltip and the point's would
  // otherwise stack on top of each other where a marker sits on a session.
  const activeTooltipPoint =
    useCustomTooltip && tooltip.visible && !hoveredMarker
      ? tooltipPoints?.[tooltip.index]
      : undefined;

  const p = theme.palette as any;
  const lineColor: string = p.blue?.[600] ?? "#5C6BC0";
  const trendColor: string = p.purple?.[400] ?? "#AB47BC";
  const masteryLineColor: string = p.green?.[500] ?? "#4CAF50";
  const axisColor: string = p.grayscale?.[600] ?? "#798397";
  // Marker colors per the Figma spec: phase lines in secondary text, external
  // events in the brand primary (matching the Add Marker dialog's line samples).
  const phaseLineColor: string = theme.palette.text.secondary;
  const externalEventColor: string = theme.palette.primary.main;

  const trendData = useMemo(
    () => (showTrendLine ? buildTrendData(data) : null),
    [data, showTrendLine]
  );

  // ------ Chart data ------
  const chartData: ChartData<"line"> = useMemo(() => {
    const datasets: ChartData<"line">["datasets"] = [
      {
        label: title,
        data,
        borderColor: lineColor,
        borderWidth: 2,
        fill: false,
        pointRadius: 4,
        pointHoverRadius: 6,
        pointBackgroundColor: lineColor,
        pointBorderColor: theme.palette.background?.paper ?? "#fff",
        pointBorderWidth: 1.5,
        tension: 0,
        order: 1,
      },
    ];

    if (trendData) {
      datasets.push({
        label: "Trend Line",
        data: trendData,
        borderColor: trendColor,
        borderWidth: 1.5,
        borderDash: [6, 4],
        fill: false,
        pointRadius: 0,
        pointHoverRadius: 0,
        tension: 0,
        order: 2,
      });
    }

    return { labels, datasets };
  }, [labels, data, title, lineColor, trendData, trendColor, theme]);

  // ------ Chart options ------
  const chartOptions: ChartOptions<"line"> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "point" as const, intersect: true },
      onHover: (_event, elements) => {
        onActivePointChange?.(elements.length > 0 ? elements[0].index : null);
      },
      // Disabled intentionally — clinical data charts don't benefit from
      // intro animations and the instant render avoids a flash on re-mount.
      animation: false,
      layout: {
        padding: {
          top: 8,
          right: 8,
          bottom: phaseZones.length > 0 ? 40 : 0,
          left: 0,
        },
      },
      scales: {
        x: {
          display: true,
          // Half a category of room on each side, so a phase line on the last
          // session sits half a step past it instead of being clamped back
          // onto the point at the plot's right edge.
          offset: true,
          grid: { display: false },
          border: { display: false },
          ticks: {
            color: axisColor,
            font: { size: 12, family: fontFamily },
            maxRotation: 0,
            padding: 4,
          },
          // No x-axis title — the Date/Session toggle in the header already
          // names what the axis shows, so a title would just duplicate it.
        },
        y: {
          display: true,
          min: 0,
          // Percentage axis is pinned to 100; count/rate axes auto-scale.
          ...(yScale === "percentage" ? { max: 100 } : {}),
          grid: { color: theme.palette.divider },
          border: { display: false },
          ticks: {
            // Fixed 10-step ticks for percentages; let Chart.js choose for auto.
            ...(yScale === "percentage" ? { stepSize: 10 } : {}),
            color: axisColor,
            font: { size: 12, family: fontFamily },
            padding: 4,
          },
          title: {
            display: true,
            text: yAxisLabel,
            color: axisColor,
            font: { size: 12, family: fontFamily },
          },
        },
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          // The designed HTML tooltip takes over when tooltipPoints is supplied;
          // otherwise Chart.js draws its default styled tooltip.
          enabled: !useCustomTooltip,
          external: useCustomTooltip ? handleExternalTooltip : undefined,
          mode: "point",
          intersect: true,
          backgroundColor: theme.palette.background?.paper ?? "#fff",
          titleColor: theme.palette.text.primary,
          bodyColor: theme.palette.text.secondary,
          borderColor: theme.palette.divider,
          borderWidth: 1,
          cornerRadius: 8,
          padding: 10,
          titleFont: { size: 12, family: fontFamily },
          bodyFont: { size: 12, family: fontFamily },
        },
      },
    }),
    [
      axisColor,
      yAxisLabel,
      yScale,
      theme,
      phaseZones,
      fontFamily,
      onActivePointChange,
      useCustomTooltip,
      handleExternalTooltip,
    ]
  );

  // ------ Plugins ------
  const phasePlugin = useMemo(
    () => buildPhaseZonePlugin(phaseZones, theme.palette.divider, fontFamily, axisColor),
    // fontFamily is a module-level constant so it never changes — omitted from deps intentionally.
    [phaseZones, theme.palette.divider, axisColor, fontFamily]
  );

  const masteryPlugin = useMemo(
    () =>
      buildMasteryCriterionPlugin(
        masteryCriterion,
        masteryCriterionLabel ?? (masteryCriterion != null ? `${masteryCriterion}% Mastery` : ""),
        masteryLineColor,
        fontFamily
      ),
    // fontFamily is a module-level constant so it never changes — omitted from deps intentionally.
    [masteryCriterion, masteryCriterionLabel, masteryLineColor, fontFamily]
  );

  const markerPlugin = useMemo(
    // Reads markers from markersRef
    () =>
      buildMarkerPlugin(
        () => markersRef.current,
        { "phase-change": phaseLineColor, "external-event": externalEventColor },
        getHoveredMarkerId
      ),
    [phaseLineColor, externalEventColor, getHoveredMarkerId]
  );

  return (
    <Box
      component="section"
      aria-label="Target chart"
      sx={[
        {
          display: "flex",
          flexDirection: "column",
          gap: 4,
          bgcolor: "background.paper",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 3,
          p: 6,
          width: "100%",
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      {/* Header row */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="flex-end"
        spacing={2}
        sx={{ width: "100%" }}
      >
        <Typography variant="body.caption" color="text.secondary" sx={{ flex: 1 }}>
          {title}
        </Typography>

        {onAddMarker && (
          <Button
            variant="text"
            size="small"
            color="primary"
            startIcon={<Flag size={16} />}
            onClick={onAddMarker}
          >
            Add Marker
          </Button>
        )}

        {onToggleXAxisMode && (
          <Select
            value={xAxisMode}
            onChange={(event: SelectChangeEvent) =>
              onToggleXAxisMode(event.target.value as XAxisMode)
            }
            size="small"
            inputProps={{ "aria-label": "X-axis display" }}
            sx={{ minWidth: 110 }}
          >
            {X_AXIS_MODE_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        )}

        {onToggleTrendLine && (
          <FormControlLabel
            label={
              <Typography variant="body.small" fontWeight={500}>
                Trend Line
              </Typography>
            }
            control={
              <Switch
                checked={showTrendLine}
                onChange={(_, checked) => onToggleTrendLine(checked)}
                size="medium"
                color="primary"
              />
            }
            labelPlacement="end"
            sx={{ ml: 0 }}
          />
        )}
      </Stack>

      {/* Chart area */}
      <Box
        aria-label="Chart area"
        sx={{
          position: "relative",
          width: "100%",
          height: 320,
          cursor: hoveredMarkerId != null ? "pointer" : undefined,
        }}
        onMouseMove={markerPointerHandlers.onMouseMove}
        onClick={markerPointerHandlers.onClick}
        onMouseLeave={() => {
          onActivePointChange?.(null);
          markerPointerHandlers.onMouseLeave();
        }}
      >
        <Line
          ref={setChartRef}
          data={chartData}
          options={chartOptions}
          plugins={[phasePlugin, masteryPlugin, markerPlugin]}
        />

        {/* Actions for a clicked marker line — mounted from the first click on,
            so it always has a position. Items follow the handlers given. */}
        {markerMenu && (
          <MarkerActionsMenu
            open={markerMenu.open}
            anchorPosition={markerMenu.anchorPosition}
            onClose={closeMarkerMenu}
            onEdit={onEditMarker ? () => onEditMarker(markerMenu.markerId) : undefined}
            onDelete={onDeleteMarker ? () => onDeleteMarker(markerMenu.markerId) : undefined}
          />
        )}

        {/* Hovered marker line — its label, date and notes, which the chart
            itself has no room to show. Anchored to the line, above the pointer. */}
        {hoveredMarker && (
          <Box
            role="tooltip"
            sx={{
              position: "absolute",
              left: hoveredMarker.x,
              top: hoveredMarker.y,
              transform: "translate(-50%, calc(-100% - 12px))",
              pointerEvents: "none",
              zIndex: 5,
              display: "flex",
              flexDirection: "column",
              gap: "4px",
              px: "12px",
              py: "8px",
              borderRadius: "4px",
              bgcolor: "background.paper",
              boxShadow: TOOLTIP_SHADOW,
              // Wide enough for the meta line and a short note to read across,
              // without spanning half the chart. `max-content` because an
              // absolutely positioned box otherwise shrinks to the space left of
              // the container's right edge, wrapping the rows early — the
              // centring transform is applied after that width is settled.
              width: "max-content",
              minWidth: 180,
              maxWidth: 300,
            }}
          >
            {/* The label leads — it's what the clinician wrote — with the type,
                date and reach beneath it, and the notes last. */}
            {hoveredMarker.marker.label && (
              <Typography sx={{ ...TOOLTIP_TEXT_SX, fontWeight: 600 }}>
                {hoveredMarker.marker.label}
              </Typography>
            )}

            <Stack direction="row" alignItems="center" spacing="4px">
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: "100px",
                  bgcolor:
                    hoveredMarker.marker.variant === "phase-change"
                      ? phaseLineColor
                      : externalEventColor,
                  flexShrink: 0,
                }}
              />
              <Typography sx={{ ...TOOLTIP_TEXT_SX, color: "text.secondary" }}>
                {[
                  MARKER_TYPE_LABEL[hoveredMarker.marker.variant],
                  hoveredMarker.marker.dateLabel,
                  (hoveredMarker.marker.appliedTargetCount ?? 1) > 1
                    ? `Applies to ${hoveredMarker.marker.appliedTargetCount} targets`
                    : null,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </Typography>
            </Stack>

            {hoveredMarker.marker.notes && (
              <Typography sx={TOOLTIP_TEXT_SX}>{hoveredMarker.marker.notes}</Typography>
            )}
          </Box>
        )}

        {/* Designed hover tooltip — positioned at the hovered point's caret. */}
        {activeTooltipPoint && (
          <Box
            role="tooltip"
            sx={{
              position: "absolute",
              left: tooltip.x,
              top: tooltip.y,
              transform: "translate(-50%, calc(-100% - 12px))",
              pointerEvents: "none",
              zIndex: 5,
              display: "flex",
              flexDirection: "column",
              gap: "8px",
              px: "12px",
              py: "8px",
              borderRadius: "4px",
              bgcolor: "background.paper",
              boxShadow: TOOLTIP_SHADOW,
              whiteSpace: "nowrap",
            }}
          >
            <Typography sx={TOOLTIP_TEXT_SX}>{activeTooltipPoint.sessionLabel}</Typography>

            {activeTooltipPoint.dateLabel && (
              <Typography sx={TOOLTIP_TEXT_SX}>{activeTooltipPoint.dateLabel}</Typography>
            )}

            {/* Series block — dot + clinician (when available), value beneath.
                Without a clinician the dot sits on the value row instead. */}
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              {activeTooltipPoint.clinicianLabel && (
                <Stack direction="row" alignItems="center" spacing="4px">
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: "100px",
                      bgcolor: lineColor,
                      flexShrink: 0,
                    }}
                  />
                  <Typography sx={TOOLTIP_TEXT_SX}>{activeTooltipPoint.clinicianLabel}</Typography>
                </Stack>
              )}

              <Stack
                direction="row"
                alignItems="center"
                spacing="4px"
                // Indent under the clinician label (past the 6px dot + gap); when
                // there's no clinician the value row carries the dot itself.
                sx={activeTooltipPoint.clinicianLabel ? { pl: "10px" } : undefined}
              >
                {!activeTooltipPoint.clinicianLabel && (
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: "100px",
                      bgcolor: lineColor,
                      flexShrink: 0,
                    }}
                  />
                )}
                <Typography sx={TOOLTIP_TEXT_SX}>{activeTooltipPoint.valueLabel}</Typography>
              </Stack>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};
export default TargetChart;
